import { CONFIG_SERVICE_KEYS } from '@web-platform/shared-universal-constants-config';
import { getConfigServiceValue } from '@web-platform/shared-universal-utils-frontend/src/config';
import { createSelector } from 'reselect';

import { getLocation } from '../state';

import type { configValueType } from '@web-platform/shared-universal-types/src/webappParams';

export const RESULTS_VIEW_QUERY_PARAM = 'resultsView';

export const RESULTS_VIEW = {
  CITIES: 'cities',
  COUNTRIES: 'countries',
} as const;

export type ResultsView = (typeof RESULTS_VIEW)[keyof typeof RESULTS_VIEW];

// Variants for the CE_City_Country_Poc experiment (STAR-300).
export const CITY_COUNTRY_SWITCH_VARIANT = {
  CONTROL: '',
  CITIES_DEFAULT: 'citiesDefault',
  COUNTRIES_DEFAULT: 'countriesDefault',
} as const;

export type CityCountrySwitchVariant =
  (typeof CITY_COUNTRY_SWITCH_VARIANT)[keyof typeof CITY_COUNTRY_SWITCH_VARIANT];

export const isCityCountrySwitchEnabled = (variant?: configValueType): boolean =>
  variant === CITY_COUNTRY_SWITCH_VARIANT.CITIES_DEFAULT ||
  variant === CITY_COUNTRY_SWITCH_VARIANT.COUNTRIES_DEFAULT;

export const getDefaultResultsView = (variant?: configValueType): ResultsView =>
  variant === CITY_COUNTRY_SWITCH_VARIANT.CITIES_DEFAULT
    ? RESULTS_VIEW.CITIES
    : RESULTS_VIEW.COUNTRIES;

// Shared by getResultsView and CityCountrySwitch so both agree on which view is active,
// including the variant's default view when no resultsView param is present (STAR-395).
export const resolveResultsView = (
  search: string,
  cityCountrySwitchVariant?: configValueType,
): ResultsView => {
  const params = new URLSearchParams(search);
  const requestedView = params.get(RESULTS_VIEW_QUERY_PARAM);

  if (requestedView === RESULTS_VIEW.CITIES || requestedView === RESULTS_VIEW.COUNTRIES) {
    return requestedView;
  }

  return getDefaultResultsView(cityCountrySwitchVariant);
};

export const getResultsView = createSelector([getLocation], (location) => {
  const cityCountrySwitchVariant = getConfigServiceValue(
    CONFIG_SERVICE_KEYS.EXPERIMENT.EXPLORE_CITY_COUNTRY_POC_VARIANT,
  );

  return resolveResultsView(location.search, cityCountrySwitchVariant);
});
